import React, { memo } from "react";
import { Tooltip, tooltipClasses, TooltipProps, Checkbox } from "@mui/material";
import { styled } from "@mui/material/styles";
import { toAbsoluteUrl } from "../../../helpers/assetHelper";
import Text from "../../../styleguide/Text";
import useTableCtx from "../tableContext/useTableCtx";
import {
  BadgeType,
  TableItemType,
  TableTextType,
} from "../tableContext/TableContext";
import { getBadgeClasses } from "../../../theme/semanticClasses";

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: 20,
    maxWidth: 500,
  },
});

export const TooltipWrapper = memo(
  ({
    children,
    tooltip,
  }: {
    children: React.ReactNode;
    tooltip?: React.ReactNode | string;
  }) => {
    if (tooltip) {
      return (
        <CustomWidthTooltip arrow title={tooltip}>
          <div>{children}</div>
        </CustomWidthTooltip>
      );
    }
    return <div>{children}</div>;
  },
);

export const TitleCell = memo(
  ({
    title,
    titleStyle,
    titleClassName,
  }: {
    title: TableTextType;
    titleStyle?: any;
    titleClassName?: string;
  }) => {
    if (typeof title === "object") {
      return (
        <>
          {title
            .filter((titleItem) => titleItem.text)
            .map((titleItem, index) => (
              <Text
                key={`${titleItem.text}-${index}`}
                component="p"
                variant="body1"
                style={{ ...titleStyle, ...titleItem.textStyle }}
                className={`mb-1 text-sm font-semibold text-slate-800 transition-colors group-hover:text-brand last:mb-0 ${
                  titleClassName ?? ""
                } ${titleItem.textClassName ?? ""}`}>
                {titleItem.text}
              </Text>
            ))}
        </>
      );
    }

    return (
      <Text
        component="p"
        variant="body1"
        style={titleStyle}
        className={`mb-0 text-sm font-semibold text-slate-800 transition-colors group-hover:text-brand ${
          titleClassName ?? ""
        }`}>
        {title}
      </Text>
    );
  },
);

export const SubtitleCell = memo(
  ({
    subtitle,
    subtitleStyle,
    subtitleClassName,
  }: {
    subtitle: TableTextType;
    subtitleStyle?: any;
    subtitleClassName?: string;
  }) => {
    if (typeof subtitle === "object") {
      return (
        <>
          {subtitle
            .filter((subtitleItem) => subtitleItem.text)
            .map((subtitleItem, index) => (
              <Text
                key={`${subtitleItem.text}-${index}`}
                component="p"
                variant="body1"
                style={{ ...subtitleStyle, ...subtitleItem.textStyle }}
                className={`mb-1 text-sm font-medium text-muted transition-colors group-hover:text-brand last:mb-0 ${
                  subtitleClassName ?? ""
                } ${subtitleItem.textClassName ?? ""}`}>
                {subtitleItem.text}
              </Text>
            ))}
        </>
      );
    }

    return (
      <Text
        component="p"
        variant="body1"
        style={subtitleStyle}
        className={`mb-0 text-sm font-medium text-muted transition-colors group-hover:text-brand ${
          subtitleClassName ?? ""
        }`}>
        {subtitle}
      </Text>
    );
  },
);

export const BadgesCell = memo(({ badges }: { badges: BadgeType[] }) => (
  <div className="inline-flex w-max max-w-full flex-col gap-2">
    {badges
      .filter((badge) => badge.badgeText)
      .map((badge, idx) => (
        <TooltipWrapper
          key={`${idx}-${badge.badgeText}`}
          tooltip={badge?.badgeTooltip}>
          <Text
            component="span"
            variant="body2"
            className={`inline-flex w-full items-center justify-center rounded-control px-3 py-2 text-center text-sm font-semibold leading-5 ${getBadgeClasses(
              badge?.badgeColor,
            )} ${badge?.className ?? ""}`}
            style={badge?.style}>
            {badge.badgeText}
          </Text>
        </TooltipWrapper>
      ))}
  </div>
));

const TableCheckbox = memo(
  ({
    checkbox,
    crudData,
    tableItemId,
    handleCheckboxClick,
  }: {
    checkbox: { isSelected: boolean; id?: string | number };
    crudData: any;
    tableItemId: string | number;
    handleCheckboxClick: (
      crudData: object,
      tableItemId?: string | number,
      checkboxId?: string | number,
    ) => void;
  }) => (
    <Checkbox
      checked={checkbox?.isSelected || false}
      tabIndex={-1}
      disableRipple
      sx={{ p: "6px", "& .MuiSvgIcon-root": { fontSize: 32 } }}
      onClick={() => handleCheckboxClick(crudData, checkbox?.id, tableItemId)}
      disabled={false}
      inputProps={{
        "aria-labelledby": `enhanced-table-checkbox-${tableItemId}`,
      }}
    />
  ),
);

function TableItem({
  tableItemData,
  tableItemId,
  crudData,
  ActionButtonsComponent,
}: {
  tableItemData: TableItemType[];
  tableItemId: string | number;
  crudData: any;
  ActionButtonsComponent?: any;
}) {
  const tableContext = useTableCtx()!;
  const { handleCheckboxClick } = tableContext;

  return (
    <tr className="group transition-colors hover:bg-surface-hover">
      {tableItemData.map((item, idx, items) => (
        <td
          key={idx}
          {...item?.config}
          className={`${
            idx === 0 ? "pl-3" : idx === items.length - 1 ? "pr-3" : ""
          }`}>
          <div className="flex items-center">
            {item?.img && (
              <div className="mr-5 h-[50px] w-[50px] shrink-0">
                <img
                  src={toAbsoluteUrl(item.img)}
                  alt={item?.title?.toString()}
                  className="mr-3 h-full w-full rounded-control object-cover"
                />
              </div>
            )}
            <div className="flex justify-start flex-col">
              {item?.title && (
                <TitleCell
                  title={item.title}
                  titleStyle={item.titleStyle}
                  titleClassName={item.titleClassName}
                />
              )}
              {item?.subtitle && (
                <SubtitleCell
                  subtitle={item.subtitle}
                  subtitleStyle={item.subtitleStyle}
                  subtitleClassName={item.subtitleClassName}
                />
              )}
              {item?.badges && item.badges.length > 0 && (
                <BadgesCell badges={item.badges} />
              )}
            </div>
            {item?.checkbox && !item?.checkbox?.isHidden && (
              <TableCheckbox
                checkbox={item.checkbox}
                crudData={crudData}
                tableItemId={tableItemId}
                handleCheckboxClick={handleCheckboxClick}
              />
            )}
          </div>
        </td>
      ))}
      {ActionButtonsComponent && (
        <td className="ml-auto rounded-r-control text-right [&>div]:inline-flex [&>div]:flex-wrap [&>div]:justify-end [&>div]:gap-3 [&>span]:inline-flex [&>span]:flex-wrap [&>span]:justify-end [&>span]:gap-3 [&_button]:!mb-0 [&_button]:!ml-0 [&_button]:!mr-0 [&_button]:!h-auto [&_button]:!min-h-[34px] [&_button]:!min-w-[68px] [&_button]:!px-4 [&_button]:!py-2">
          <ActionButtonsComponent
            itemId={tableItemId}
            crudData={crudData}
            tableItemData={tableItemData}
            tableContext={tableContext}
          />
        </td>
      )}
    </tr>
  );
}

export default memo(TableItem);
